import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Mail, Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PublicLayout from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const EMPTY = { first_name: "", last_name: "", email: "", phone: "", city: "", num_properties: "", plan: "", message: "", privacy_consent: false };

export default function Contatti() {
  const [form, setForm] = useState(EMPTY);
  const [params] = useSearchParams();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const plan = params.get("plan");
    const units = params.get("units");
    if (plan || units) {
      const alloggi = units ? ` per ${units} ${units === "1" ? "alloggio" : "alloggi"}` : "";
      setForm((f) => ({
        ...f,
        plan: plan || f.plan,
        num_properties: units || f.num_properties,
        message: `Sono interessato al piano ${plan || "Quattro"}${alloggi}. Vorrei ricevere un preventivo.`,
      }));
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if ((form.phone.match(/\d/g) || []).length < 6) { toast.error("Inserisci un numero di telefono valido"); return; }
    if (!form.privacy_consent) { toast.error("Devi accettare la Privacy Policy"); return; }
    setSending(true);
    try {
      await api.post("/public/contact", form);
      setSent(true);
      toast.success("Richiesta inviata! Ti ricontatteremo al più presto.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore nell'invio");
    } finally {
      setSending(false);
    }
  };

  return (
    <PublicLayout>
      <section className="bg-brand-plum text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <p className="text-xs font-mono uppercase tracking-[0.22em] font-semibold text-brand-tealGlow mb-4">Parliamone</p>
          <h1 className="text-4xl sm:text-5xl font-serif">Richiedi il tuo preventivo</h1>
          <p className="mt-4 text-white/75 max-w-2xl">Raccontaci della tua struttura: ti prepareremo una proposta su misura senza impegno.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          {sent ? (
            <div className="rounded-3xl bg-white border border-brand-plum/10 p-12 text-center" data-testid="contact-success">
              <CheckCircle2 className="mx-auto text-brand-teal mb-4" size={48} />
              <h2 className="font-serif text-3xl text-brand-plum mb-2">Grazie!</h2>
              <p className="text-brand-plum/60 mb-6">La tua richiesta è stata inviata. Ti ricontatteremo entro 24 ore.</p>
              <Button onClick={() => setSent(false)} variant="outline" className="rounded-full border-brand-plum/20 text-brand-plum">Invia un'altra richiesta</Button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="rounded-3xl bg-white border border-brand-plum/10 p-6 md:p-8 shadow-sm space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-brand-plum">Nome *</Label>
                  <Input required value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="contact-first-name" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-brand-plum">Cognome *</Label>
                  <Input required value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="contact-last-name" className="mt-1.5" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-brand-plum">Email *</Label>
                  <Input required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-brand-plum">Telefono *</Label>
                  <Input required type="tel" pattern="\+?[\d\s\.\(\)\-]{6,20}" title="Inserisci un numero di telefono valido (es. +39 333 1234567)" placeholder="+39 333 1234567" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" className="mt-1.5" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-brand-plum">Città</Label>
                  <Input value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="contact-city" className="mt-1.5" />
                </div>
                <div>
                  <Label className="text-brand-plum">Numero di case/appartamenti</Label>
                  <Input value={form.num_properties} onChange={(e) => set("num_properties", e.target.value)} data-testid="contact-num-properties" className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label className="text-brand-plum">Messaggio</Label>
                <Textarea rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" className="mt-1.5" placeholder="Raccontaci della tua struttura..." />
              </div>
              <div className="flex items-start gap-3">
                <Checkbox id="privacy" checked={form.privacy_consent} onCheckedChange={(v) => set("privacy_consent", !!v)} data-testid="contact-privacy-consent" className="mt-0.5" />
                <label htmlFor="privacy" className="text-sm text-brand-plum/70 leading-relaxed">
                  Ho letto e accetto la Privacy Policy e acconsento al trattamento dei miei dati per essere ricontattato. *
                </label>
              </div>
              <Button type="submit" disabled={sending} data-testid="contact-submit" className="w-full rounded-full bg-brand-teal hover:bg-brand-tealDark text-brand-violet font-semibold h-12">
                <Send size={16} className="mr-2" /> {sending ? "Invio in corso..." : "Richiedi preventivo"}
              </Button>
            </form>
          )}
        </div>

        <div className="space-y-4">
          {[
            { icon: Mail, label: "Email", value: "info@quattro.house" },
            { icon: Phone, label: "Telefono", value: "+39 3334975115" },
            { icon: MapPin, label: "Sede", value: "Via L. Cavaliero 92, Salerno 84125" },
          ].map((c) => (
            <div key={c.label} className="rounded-2xl bg-white border border-brand-plum/10 p-6 flex items-center gap-4">
              <span className="w-11 h-11 rounded-xl bg-brand-tealLight flex items-center justify-center shrink-0"><c.icon className="text-brand-tealDark" size={20} /></span>
              <div>
                <p className="text-xs text-brand-plum/50">{c.label}</p>
                <p className="font-medium text-brand-plum">{c.value}</p>
              </div>
            </div>
          ))}
          <div className="rounded-2xl bg-brand-violet p-6 text-white">
            <h3 className="font-serif text-xl mb-2">Prenotazione diretta</h3>
            <p className="text-sm text-white/70">Zero intermediazione: la relazione con i tuoi ospiti resta sempre tua.</p>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
